import { Injectable } from '@angular/core'
import {
  RootState,
  selectIsInternalUser,
  setActiveSavedViews,
  setActiveWorkspace,
  setWorkspacesLoaded,
} from '@helios/state-management'
import { Store } from '@ngrx/store'
import { BARE_WORKSPACE } from '../preset-workspaces/preset-workspaces'
import { WorkspaceDelegateService } from './workspace-delegate.service'
import { take } from 'rxjs/operators'

const DEFAULT_RECON_PRESET_PREFIX =
  'workspace/preset/a8f584ce-0576-4adc-8e34-9d022ff1f1a2'
const DEFAULT_EXTERNAL_PRESET_PREFIX =
  'workspace/preset/aa270e18-d80a-4d8f-9ce9-7df38115a5e5'

/**
 * The purpose of this service is to load the workspace slice of the ngrx store prior to the app being used.
 * This should prevent race conditions and weird behavior with dependencies like the Workspace Catalog and all of the
 * grid panels.
 */
@Injectable({
  providedIn: 'root',
})
export class WorkspaceStoreLoaderService {
  constructor(
    private readonly service: WorkspaceDelegateService,
    private readonly store: Store<RootState>,
  ) {}

  async loadStore(retryCount = 0): Promise<void> {
    let defaultWorkspace = await this.service.getDefaultWorkspace()
    if (defaultWorkspace == null) {
      await this.createDefaultFromBase()
      defaultWorkspace = await this.service.getDefaultWorkspace()
    }
    if (defaultWorkspace == null) {
      console.error('Failed to load store.')
      this.service.triggerFailover('Failed to load default workspace.')
      return retryCount === 0 ? this.loadStore(1) : undefined
    }
    const savedViews = await this.service.getSavedViews(
      defaultWorkspace.workspacePrefix,
    )
    this.store.dispatch(setActiveWorkspace(defaultWorkspace))
    this.store.dispatch(setActiveSavedViews(savedViews))
    this.store.dispatch(setWorkspacesLoaded())
  }

  private async createDefaultFromBase(): Promise<void> {
    const isInternal = await this.store
      .select(selectIsInternalUser)
      .pipe(take(1))
      .toPromise()
    const preset = isInternal
      ? DEFAULT_RECON_PRESET_PREFIX
      : DEFAULT_EXTERNAL_PRESET_PREFIX

    let newWorkspacePrefix: string | undefined =
      await this.service.cloneWorkspace(preset)

    if (newWorkspacePrefix == null) {
      newWorkspacePrefix = await this.service.createWorkspace(BARE_WORKSPACE) // fail-safe, should never happen
    }

    await this.service.makeWorkspaceDefault(newWorkspacePrefix)
  }
}
